import {StateCreator} from "zustand/vanilla"
import {WorkspaceChat} from "@/pb/api/devplan/types/workspace_chats"
import {
  CreateWorkspaceChatRequest,
  CreateWorkspaceChatResponse,
  GenerateWorkspaceChatTitleRequest,
  GenerateWorkspaceChatTitleResponse,
  GetWorkspaceChatActionPromptResponse,
  GetWorkspaceChatResponse,
  ListWorkspaceChatsResponse,
  UpdateWorkspaceChatRequest,
  UpdateWorkspaceChatResponse,
} from "@/pb/api/devplan/services/web/company"
import {WorkspaceChatDetails} from "@/pb/api/devplan/types/workspace_chats"
import {chatsAPI, chatAPI, chatGenerateTitleAPI, workspaceChatActionPromptAPI} from "@/app/apiPaths"
import {jr} from "@/hooks/queries/response-handler"

export type WorkspaceChatStoreState = {
  workspaceChats: Record<number, string[]>
  workspaceChatById: Record<string, WorkspaceChat>
  fetchWorkspaceChats(companyId: number): Promise<void>
  fetchWorkspaceChat(companyId: number, chatId: string): Promise<void>
  createWorkspaceChat(companyId: number, details?: WorkspaceChatDetails): Promise<WorkspaceChatMutationResult>
  fetchWorkspaceChatActionPrompt(companyId: number, actionId: string): Promise<string>
  updateWorkspaceChatTitle(companyId: number, chatId: string, title: string): Promise<void>
  setWorkspaceChatFavorite(companyId: number, chatId: string, favorite: boolean): Promise<void>
  deleteWorkspaceChat(companyId: number, chatId: string): Promise<void>
  generateWorkspaceChatTitle(companyId: number, chatId: string, message: string): Promise<void>
}

export interface WorkspaceChatMutationResult {
  chat: WorkspaceChat
  warning?: string
}

export const createWorkspaceChatSlice: StateCreator<
  WorkspaceChatStoreState,
  [],
  [],
  WorkspaceChatStoreState
> = ((set) => ({
  workspaceChats: {},
  workspaceChatById: {},
  fetchWorkspaceChats: async (companyId) => {
    const js = await fetch(chatsAPI(companyId)).then(jr)
    const {chats} = ListWorkspaceChatsResponse.fromJSON(js)
    const byId: Record<string, WorkspaceChat> = {}
    chats.forEach(c => byId[c.id] = c)
    set(s => ({
      ...s,
      workspaceChats: {...s.workspaceChats, [companyId]: chats.map(c => c.id)},
      workspaceChatById: {...s.workspaceChatById, ...byId},
    }))
  },
  fetchWorkspaceChat: async (companyId, chatId) => {
    const js = await fetch(chatAPI(companyId, chatId)).then(jr)
    const {chat} = GetWorkspaceChatResponse.fromJSON(js)
    if (chat) {
      set(s => ({
        ...s,
        workspaceChatById: {...s.workspaceChatById, [chatId]: chat},
      }))
    }
  },
  createWorkspaceChat: async (companyId, details) => {
    const js = await fetch(chatsAPI(companyId), {
      method: 'POST',
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify(CreateWorkspaceChatRequest.toJSON({companyId, details})),
    }).then(jr)
    const {chat, warning} = CreateWorkspaceChatResponse.fromJSON(js)
    if (!chat) throw new Error("Failed to create chat: empty response")
    set(s => ({
      ...s,
      workspaceChats: {...s.workspaceChats, [companyId]: [chat.id, ...(s.workspaceChats[companyId] ?? [])]},
      workspaceChatById: {...s.workspaceChatById, [chat.id]: chat},
    }))
    return {chat, warning}
  },
  fetchWorkspaceChatActionPrompt: async (companyId, actionId) => {
    const js = await fetch(workspaceChatActionPromptAPI(companyId, actionId)).then(jr)
    const {prompt} = GetWorkspaceChatActionPromptResponse.fromJSON(js)
    if (!prompt.trim()) throw new Error("This action has no configured prompt. Ask an administrator to configure it and try again.")
    return prompt
  },
  updateWorkspaceChatTitle: async (companyId, chatId, title) => {
    const js = await fetch(chatAPI(companyId, chatId), {
      method: 'PATCH',
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify(UpdateWorkspaceChatRequest.toJSON({id: chatId, companyId, title})),
    }).then(jr)
    const {chat} = UpdateWorkspaceChatResponse.fromJSON(js)
    if (!chat) throw new Error("Failed to update chat title: empty response")
    set(s => ({
      ...s,
      workspaceChatById: {...s.workspaceChatById, [chatId]: chat},
    }))
  },
  setWorkspaceChatFavorite: async (companyId, chatId, favorite) => {
    const js = await fetch(chatAPI(companyId, chatId), {
      method: 'PATCH',
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify(UpdateWorkspaceChatRequest.toJSON({id: chatId, companyId, favorite})),
    }).then(jr)
    const {chat} = UpdateWorkspaceChatResponse.fromJSON(js)
    if (!chat) throw new Error("Failed to update chat favorite: empty response")
    set(s => ({
      ...s,
      workspaceChatById: {...s.workspaceChatById, [chatId]: chat},
    }))
  },
  deleteWorkspaceChat: async (companyId, chatId) => {
    await fetch(chatAPI(companyId, chatId), {method: 'DELETE'}).then(jr)
    set(s => {
      const {[chatId]: _, ...restById} = s.workspaceChatById
      const updatedIds = (s.workspaceChats[companyId] ?? []).filter(id => id !== chatId)
      return {
        ...s,
        workspaceChatById: restById,
        workspaceChats: {...s.workspaceChats, [companyId]: updatedIds},
      }
    })
  },
  generateWorkspaceChatTitle: async (companyId, chatId, message) => {
    const js = await fetch(chatGenerateTitleAPI(companyId, chatId), {
      method: 'POST',
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify(GenerateWorkspaceChatTitleRequest.toJSON({message})),
    }).then(jr)
    const {chat} = GenerateWorkspaceChatTitleResponse.fromJSON(js)
    if (chat) {
      set(s => ({
        ...s,
        workspaceChatById: {...s.workspaceChatById, [chatId]: chat},
      }))
    }
  },
}))
